import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { CheckCircle, Circle, Rocket, ArrowRight } from "@phosphor-icons/react";

// First-login welcome for a freshly provisioned workspace admin: the setup checklist as a dialog (the dashboard widget carries on afterwards).
export default function WelcomeChecklistDialog() {
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const eligible = Boolean(user?.welcome_pending) && isAdmin && !user?.is_sandbox && !user?.impersonating && !user?.is_platform_admin;

  useEffect(() => {
    if (!eligible) return;
    API.get("/tenant/onboarding-progress").then(({ data }) => { if (!data.dismissed) { setData(data); setOpen(true); } }).catch(() => {});
  }, [eligible]);

  const close = async () => {
    setOpen(false);
    try { await API.post("/tenant/welcome-seen"); await checkAuth(); } catch {}
  };

  if (!eligible || !data) return null;
  const next = data.items.find((i) => !i.done);
  const firstName = (user?.name || "").split(" ")[0];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg" data-testid="welcome-checklist-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Chivo]">
            <span className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Rocket size={18} weight="duotone" /></span>
            Welcome{firstName ? `, ${firstName}` : ""} — let's get {data.tenant_name || "your workspace"} going
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">A few quick steps turn an empty workspace into one your team and clients can use today. You can pick these up any time from the dashboard.</p>
        <div className="mt-1">
          <Progress value={data.progress} className="h-2" data-testid="welcome-checklist-progress" />
          <p className="text-[10px] text-muted-foreground mt-1">{data.done_count}/{data.total} done</p>
        </div>
        <div className="divide-y divide-border/60 border border-border rounded-md overflow-hidden" data-testid="welcome-checklist-items">
          {data.items.map((item) => (
            <Link key={item.id} to={item.link} onClick={close} className={`flex items-start gap-3 px-3 py-2.5 hover:bg-muted/40 transition-colors ${item.done ? "opacity-60" : ""}`} data-testid={`welcome-item-${item.id}`}>
              {item.done ? <CheckCircle size={18} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" /> : <Circle size={18} className="text-muted-foreground/40 shrink-0 mt-0.5" />}
              <span className="min-w-0 flex-1">
                <span className={`block text-sm ${item.done ? "line-through text-muted-foreground" : "font-medium"}`}>{item.label}</span>
                {item.hint && !item.done && <span className="block text-[11px] text-muted-foreground leading-snug">{item.hint}</span>}
              </span>
            </Link>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={close} data-testid="welcome-checklist-later">I'll do this later</Button>
          {next && (
            <Button asChild size="sm">
              <Link to={next.link} onClick={close} data-testid="welcome-checklist-start">{next.id === "upload_logo" || next.id === "set_colors" ? "Start with branding" : `Start: ${next.label}`} <ArrowRight size={14} className="ml-1" /></Link>
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
